import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Loader2, ArrowRight, Save, Image as ImageIcon } from 'lucide-react';

const CATEGORIES = [
  'تصميم الموقع',
  'برمجة',
  'تسويق',
  'تصميم واجهات',
  'أخرى'
];

export default function AdminArticleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    category: CATEGORIES[0],
    coverImageUrl: '',
    content: '',
    authorName: 'فريق لباب', // Default author
  });

  useEffect(() => {
    if (isEditing) {
      const fetchArticle = async () => {
        try {
          const docRef = doc(db, 'articles', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setFormData(docSnap.data());
          } else {
            alert('المقالة غير موجودة');
            navigate('/admin/articles');
          }
        } catch (error) {
          console.error("Error fetching article:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchArticle();
    }
  }, [id, navigate, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageUploadLoading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('image', file);

      const response = await fetch('https://api.imgbb.com/1/upload?key=c3c32fcb5862c17a9d81ad417934b0cc', {
        method: 'POST',
        body: uploadData
      });

      const data = await response.json();

      if (data.success) {
        setFormData(prev => ({ ...prev, coverImageUrl: data.data.url }));
      } else {
        throw new Error(data.error?.message || 'فشل الرفع لـ ImgBB');
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      alert('حدث خطأ أثناء رفع الصورة. تحقق من الاتصال وجرب مرة أخرى.');
    } finally {
      setImageUploadLoading(false);
    }
  };

  const generateProxyHtml = (articleId, data) => {
    const plainTextDescription = data.content.replace(/<[^>]+>/g, '').substring(0, 150).replace(/"/g, '&quot;') + '...';
    const targetUrl = `https://lobab-course.web.app/blog/${articleId}`;
    const coverImage = data.coverImageUrl || 'https://lobab-course.web.app/lobab_cover_fallback.png';
    const title = data.title.replace(/"/g, '&quot;');

    return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="utf-8">
  <title>${title} | منصة لباب</title>
  <meta name="description" content="${plainTextDescription}">
  <meta property="og:title" content="${title} | لباب">
  <meta property="og:description" content="${plainTextDescription}">
  <meta property="og:image" content="${coverImage}">
  <meta property="og:type" content="article">
  <meta property="og:url" content="${targetUrl}">
  <meta name="twitter:card" content="summary_large_image">
  <meta name="twitter:title" content="${title} | لباب">
  <meta name="twitter:description" content="${plainTextDescription}">
  <meta name="twitter:image" content="${coverImage}">
  <meta http-equiv="refresh" content="0; url=${targetUrl}">
  <script>window.location.href="${targetUrl}";</script>
</head>
<body style="background-color: #09090b; color: white; display: flex; justify-content: center; align-items: center; height: 100vh; font-family: system-ui, sans-serif;">
  <p>يتم تحويلك إلى المقالة... <a href="${targetUrl}" style="color: #6366f1;">اضغط هنا</a></p>
</body>
</html>`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.content) {
      alert('الرجاء إدخال العنوان والمحتوى');
      return;
    }

    setSaving(true);
    try {
      let articleId = id;
      if (isEditing) {
        await updateDoc(doc(db, 'articles', id), {
          ...formData,
        });
      } else {
        const newDocRef = await addDoc(collection(db, 'articles'), {
          ...formData,
          createdAt: serverTimestamp(),
          likesCount: 0,
        });
        articleId = newDocRef.id;
      }

      // Generate & Upload SEO Proxy HTML for WhatsApp/Facebook
      const htmlContent = generateProxyHtml(articleId, formData);
      const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
      const proxyRef = ref(storage, `seo-proxies/blog_${articleId}.html`);
      await uploadBytes(proxyRef, blob);
      const shareUrl = await getDownloadURL(proxyRef);

      // Save the generated shareUrl to the article
      await updateDoc(doc(db, 'articles', articleId), { shareUrl });

      navigate('/admin/articles');
    } catch (error) {
      console.error("Error saving article:", error);
      alert('حدث خطأ أثناء حفظ المقالة');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20 text-slate-500">
        <Loader2 className="w-10 h-10 animate-spin text-primary-600 mb-4" />
        <p className="font-bold">جاري تحميل المقالة...</p>
      </div>
    );
  }

  // ReactQuill modules setup for toolbar
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'direction': 'rtl' }, { 'align': [] }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'indent',
    'link', 'image',
    'direction', 'align'
  ];

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto mb-20">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/articles')}
            className="p-2 border border-slate-200 dark:border-white/10 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors text-slate-500"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-1">
              {isEditing ? 'تعديل المقالة' : 'إنشاء مقالة جديدة'}
            </h1>
            <p className="text-slate-500 dark:text-zinc-400">
              قم بصياغة مقالتك ونشرها على المدونة
            </p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-bold transition-all disabled:opacity-50 shadow-sm"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          <span>{isEditing ? 'حفظ التعديلات' : 'نشر المقالة'}</span>
        </button>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 md:p-8 shadow-sm">
        <form className="space-y-8" onSubmit={handleSubmit}>
          {/* Cover Image Upload */}
          <div>
            <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-3">الصورة البارزة</label>
            <div className="flex items-start gap-6">
              <div
                className="w-full max-w-sm aspect-[16/9] border-2 border-dashed border-slate-300 dark:border-zinc-700 rounded-2xl flex flex-col items-center justify-center bg-slate-50 dark:bg-black/50 overflow-hidden relative"
              >
                {formData.coverImageUrl ? (
                  <img src={formData.coverImageUrl} alt="Cover" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center text-slate-400">
                    <ImageIcon className="w-10 h-10 mb-2 opacity-50" />
                    <span className="font-bold text-md">ارفع صورة بحجم مناسب (16:9)</span>
                  </div>
                )}
                {imageUploadLoading && (
                  <div className="absolute inset-0 bg-white/70 dark:bg-black/70 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  id="coverImage"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <label
                  htmlFor="coverImage"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-900 dark:text-white rounded-xl font-bold transition-colors cursor-pointer"
                >
                  اختيار صورة...
                </label>
                <p className="mt-3 text-md text-slate-500 font-bold leading-relaxed max-w-xs">
                  يُفضل اختيار صورة واضحة وعالية الجودة بحجم أقصى 2MB، للحفاظ على تناسق تصميم المدونة.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-2">عنوان المقالة <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                placeholder="أدخل عنواناً جذاباً..."
                className="w-full text-lg px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
              />
            </div>

            {/* Author */}
            <div>
              <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-2">اسم الكاتب</label>
              <input
                type="text"
                name="authorName"
                value={formData.authorName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-3 flex items-center justify-between">
                <span>فئة المقالة</span>
                {formData.category && !CATEGORIES.includes(formData.category) && (
                  <span className="text-xs bg-primary-500/10 text-primary-600 px-2 py-0.5 rounded-full">فئة مخصصة</span>
                )}
              </label>

              <div className="flex flex-wrap gap-2 mb-4">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, category: cat }))}
                    className={`px-5 py-2 rounded-xl font-bold text-sm transition-all border ${formData.category === cat
                        ? 'bg-primary-600 text-white border-primary-500 shadow-md shadow-primary-500/20'
                        : 'bg-white dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 border-slate-200 dark:border-white/5 hover:border-primary-200 dark:hover:border-primary-500/20'
                      }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="relative group">
                <input
                  type="text"
                  placeholder="أو اكتب فئة جديدة يدوياً هنا..."
                  value={CATEGORIES.includes(formData.category) ? '' : formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Editor */}
          <div className="pt-4" dir="rtl">
            <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-2 text-right">المحتوى <span className="text-red-500">*</span></label>
            <div className="bg-white dark:bg-zinc-900 rounded-xl overflow-hidden border border-slate-200 dark:border-white/10 prose prose-slate dark:prose-invert max-w-none">
              <style>{`
                 .ql-toolbar.ql-snow {
                   position: sticky;
                   top: 0;
                   z-index: 20;
                   background: white !important;
                   border-top: none !important;
                   border-left: none !important;
                   border-right: none !important;
                   border-bottom: 1px solid #e2e8f0 !important;
                   text-align: right !important;
                   direction: rtl !important;
                 }
                 .dark .ql-toolbar.ql-snow {
                   background: #18181b !important;
                   border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
                 }
                 /* Quill toolbar button styling for RTL */
                 .ql-snow.ql-toolbar button, .ql-snow .ql-toolbar button {
                    float: right !important;
                 }
                 .ql-snow.ql-toolbar .ql-formats, .ql-snow .ql-toolbar .ql-formats {
                    margin-right: 0 !important;
                    margin-left: 15px !important;
                    float: right !important;
                 }
                 .ql-container.ql-snow {
                   border: none !important;
                   min-height: 400px;
                   direction: rtl !important;
                   text-align: right !important;
                 }
                 .ql-editor {
                   min-height: 400px;
                   direction: rtl !important;
                   text-align: right !important;
                 }
               `}</style>
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={handleEditorChange}
                modules={modules}
                formats={formats}
                placeholder="اكتب محتوى مقالتك هنا..."
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
